import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, BookOpen, ExternalLink, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import api from "@/lib/api";

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function getNumber(title) {
  const m = title.match(/#(\d+)/);
  return m ? parseInt(m[1]) : 0;
}

function ToileCard({ n }) {
  const hasContent = n.has_full_content;

  const inner = (
    <div className="group border rounded-xl overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all h-full flex flex-col">
      {n.featured_image && (
        <img src={n.featured_image} alt={n.title} className="w-full h-44 object-cover" />
      )}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading text-lg mb-2 group-hover:text-primary transition-colors">{n.title}</h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <Calendar className="h-3.5 w-3.5" />
          <span>{formatDate(n.publication_date)}</span>
        </div>
        <div className="mt-auto pt-2">
          {hasContent ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary group-hover:underline">
              <BookOpen className="h-3 w-3" /> Lire ce numéro
            </span>
          ) : n.newsletter_url ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary group-hover:underline">
              <ExternalLink className="h-3 w-3" /> Accéder à la Toile
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Contenu indisponible</span>
          )}
        </div>
      </div>
    </div>
  );

  if (hasContent) return <Link to={`/article/${n.slug}`} data-testid={`toile-${n.slug}`}>{inner}</Link>;
  if (n.newsletter_url) return <a href={n.newsletter_url} target="_blank" rel="noopener noreferrer" data-testid={`toile-${n.slug}`}>{inner}</a>;
  return <div data-testid={`toile-${n.slug}`}>{inner}</div>;
}

export default function LaToilePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/newsletters").then((r) => {
      const toile = (r.data || [])
        .filter((n) => n.newsletter_type === "toile")
        .sort((a, b) => getNumber(b.title) - getNumber(a.title));
      setItems(toile);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="La Toile d'OFALycée — Newsletter mensuelle"
        description="Retrouvez tous les numéros de La Toile d'OFALycée : articles, quiz, interviews et actualités de la communauté OFALycée."
        url="/la-toile"
      />
      <div data-testid="la-toile-page">
        <section className="relative overflow-hidden bg-[#14274B] text-white" style={{minHeight: '220px'}}>
          <img
            src="https://images.unsplash.com/photo-1499750310107-5fef28a66643?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920"
            alt="La Toile d'OFALycée"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14274B]/90 to-[#14274B]/60" />
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-ofa-blue/20 flex items-center justify-center">
                <Mail className="h-7 w-7 text-ofa-blue" />
              </div>
              <div>
                <span className="text-sm font-mono tracking-widest uppercase text-ofa-blue block">Newsletter</span>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl" data-testid="la-toile-title">La Toile d'OFALycée</h1>
              </div>
            </div>
            <p className="text-lg text-white/60 max-w-2xl leading-relaxed">
              Notre newsletter mensuelle : articles de fond, quiz, interviews d'élèves et actualités de la communauté OFALycée.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <a href="https://info.ofalycee.org/subscribe" target="_blank" rel="noopener noreferrer">
                <Button className="bg-ofa-blue hover:bg-ofa-blue/90 rounded-full px-6 text-white" data-testid="subscribe-toile-btn">
                  S'abonner à La Toile <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </a>
              <Link to="/newsletter" className="text-sm text-ofa-blue hover:underline">
                Voir les archives — La Quinzaine d'OFALycée →
              </Link>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Chargement...</div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 bg-primary rounded-full" />
              <h2 className="font-heading text-2xl md:text-3xl">Tous les numéros</h2>
              <span className="text-sm text-muted-foreground ml-2">({items.length} éditions)</span>
            </div>
            {items.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">Aucune édition disponible.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((n) => (
                  <ToileCard key={n.slug} n={n} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
